import React, { FC } from 'react'
import { Pressable, View } from 'react-native'

import Text from '@common/components/Text'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import { isMobileWebApp } from '@common/config/env'
import { useTranslation } from '@common/config/localization'
import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import { BORDER_RADIUS_PRIMARY } from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import { createTab, openInternalPageInTab } from '@web/extension-services/background/webapi/tab'

export type RouteItemType = {
  icon: any
  label: string
  route?: string
  /** Used with hiddenRoutes when `route` is external and does not match WEB_ROUTES */
  filterKey?: string
  disabled?: boolean
  onPress?: () => void
  testID?: string
  isExternal?: boolean
  /** Open internal route in a new tab (for complex UIs like Swap & Bridge) */
  openInTab?: boolean
  scale: number
  scaleOnHover: number
  backgroundImage?: string
  disabledTooltip?: { id: string; content: string }
}

interface Props {
  routeItem: RouteItemType
  index: number
  routeItemsLength: number
}

const ITEM_HEIGHT = 44
const ICON_SIZE = 24

const RouteItem: FC<Props> = ({ routeItem, index, routeItemsLength }) => {
  const { theme, themeType } = useTheme()
  const { t } = useTranslation()
  const { navigate } = useNavigation()
  const { addToast } = useToast()

  const shouldWrapDisabledTooltip = Boolean(routeItem.disabled && routeItem.disabledTooltip)
  const outerSpacingStyle = index !== routeItemsLength - 1 && spacings.mrSm

  const pressable = (
    <Pressable
      style={[flexbox.alignCenter, !shouldWrapDisabledTooltip ? outerSpacingStyle : undefined]}
      disabled={routeItem.disabled}
      onPress={async () => {
        if (routeItem?.onPress) {
          routeItem.onPress()
          return
        }

        if (routeItem.isExternal && routeItem.route) {
          try {
            await createTab(routeItem.route)
          } catch {
            addToast(t('Failed to open new tab.'), { type: 'error' })
          }
          return
        }
        if (routeItem.openInTab && routeItem.route) {
          try {
            await openInternalPageInTab({ route: routeItem.route })
          } catch {
            addToast(t('Failed to open new tab.'), { type: 'error' })
          }
          return
        }
        if (!routeItem.route) return

        navigate(routeItem.route)
      }}
    >
      {({ hovered }: any) => (
        <>
          <View
            testID={routeItem.testID}
            style={{
              height: ITEM_HEIGHT,
              width: ITEM_HEIGHT,
              borderRadius: BORDER_RADIUS_PRIMARY,
              backgroundColor: hovered
                ? themeType === THEME_TYPES.DARK
                  ? '#1b2b2c'
                  : '#141833CC'
                : themeType === THEME_TYPES.DARK
                  ? theme.primaryBackground
                  : theme.primaryText,
              ...flexbox.center,
              ...spacings.mbTy,
              ...(routeItem.backgroundImage
                ? {
                    // @ts-ignore
                    backgroundImage: `url(${routeItem.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }
                : {}),
              ...(routeItem.disabled && { opacity: 0.4 })
            }}
          >
            <View style={[flexbox.center, flexbox.alignCenter]}>
              <routeItem.icon
                // Action buttons sit on the mode gradient banner, so always white.
                color="#fff"
                // Rewards has no other hover effect so we slightly increase its size
                height={
                  routeItem.route === WEB_ROUTES.rewards && hovered ? ICON_SIZE + 2 : ICON_SIZE
                }
                width={
                  routeItem.route === WEB_ROUTES.rewards && hovered ? ICON_SIZE + 2 : ICON_SIZE
                }
              />
            </View>
          </View>
          <Text
            // Action button labels sit on the mode gradient banner, so always white.
            color="#fff"
            weight="regular"
            fontSize={12}
            style={[
              // Mobile-web keeps the two-line "Swap / Bridge" label (drop the "& ")
              // so its tile stays the same square width as Send/Receive instead of
              // stretching. The extension popup just shows the short "Swap" label.
              isMobileWebApp &&
                routeItem.testID === 'dashboard-button-swap-and-bridge' && {
                  textAlign: 'center'
                },
              routeItem.disabled && { opacity: 0.4 }
            ]}
          >
            {isMobileWebApp && routeItem.testID === 'dashboard-button-swap-and-bridge'
              ? 'Swap\nBridge'
              : routeItem.label}
          </Text>
        </>
      )}
    </Pressable>
  )

  if (shouldWrapDisabledTooltip && routeItem.disabledTooltip) {
    return (
      <View
        style={[flexbox.alignCenter, outerSpacingStyle]}
        dataSet={createGlobalTooltipDataSet({
          id: routeItem.disabledTooltip.id,
          content: routeItem.disabledTooltip.content,
          hidden: false
        })}
      >
        {pressable}
      </View>
    )
  }

  return pressable
}

export default React.memo(RouteItem)
